import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function MyFundraisers() {
  const navigate = useNavigate();
  const [fundraisers, setFundraisers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [summary, setSummary] = useState({
    totalFundraisers: 0,
    totalRaised: "0",
    totalDonated: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const storedUser = sessionStorage.getItem("user");
    if (!storedUser) {
      navigate("/signin");
      return;
    }

    const userData = JSON.parse(storedUser);
    setUser(userData);
    
    // Get user ID from the stored user data
    const fetchedUserId = userData.id || userData.userId;
    if (fetchedUserId) {
      setUserId(fetchedUserId);
      fetchDashboardData(fetchedUserId);
    } else {
      // If user ID is not in the stored data, fetch it using email
      fetchUserIdAndDashboard(userData.email);
    }
  }, [navigate]);

  const fetchUserIdAndDashboard = async (email) => {
    setLoading(true);
    try {
      // Get user ID using email
      const userResponse = await axios.post("https://api.donatepe.org/db/select", {
        table: "users",
        columns: ["id"],
        conditions: {
          email: email
        }
      });

      if (userResponse.data && userResponse.data.rows && userResponse.data.rows.length > 0) {
        const fetchedUserId = userResponse.data.rows[0].id;
        setUserId(fetchedUserId);
        
        // Fetch dashboard data with user ID
        await fetchDashboardData(fetchedUserId);
      } else {
        setError("User not found in database.");
        setLoading(false);
      }
    } catch (err) {
      
      setError("Failed to load user data. Please try again.");
      setLoading(false);
    }
  };

  const fetchDashboardData = async (userId) => {
    setLoading(true);
    try {
      const response = await axios.get(`https://api.donatepe.org/api/fundraiser/dashboard/${userId}`);
      
      if (response.data && response.data.success && response.data.data) {
        const dashboardData = response.data.data;
        
        // Set summary data
        setSummary(dashboardData.summary || {
          totalFundraisers: 0,
          totalRaised: "0",
          totalDonated: 0
        });
        
        // Set fundraisers
        setFundraisers(dashboardData.myFundraisers || []);
        
        // Set donations (if needed for future features)
        setDonations(dashboardData.myDonations || []);
        
        setError("");
      } else {
        setError("Invalid response format from server.");
        setFundraisers([]);
      }
    } catch (err) {
     
      setError("Failed to load fundraisers. Please try again.");
      setFundraisers([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numAmount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "draft": { color: "bg-gray-100 text-gray-800", label: "Draft" },
      "submitted": { color: "bg-yellow-100 text-yellow-800", label: "Submitted" },
      "pending": { color: "bg-yellow-100 text-yellow-800", label: "Pending" },
      "approved": { color: "bg-blue-100 text-blue-800", label: "Approved" },
      "active": { color: "bg-green-100 text-green-800", label: "Active" },
      "rejected": { color: "bg-red-100 text-red-800", label: "Rejected" },
      "completed": { color: "bg-purple-100 text-purple-800", label: "Completed" },
      "closed": { color: "bg-gray-100 text-gray-800", label: "Closed" }
    };

    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", label: status };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPurposeIcon = (purpose) => {
    // You might need to map purpose from the fundraiser data
    // For now, we'll return a default icon
    return "fa-heart";
  };

  const getPurposeLabel = (purpose) => {
    // You might need to map purpose from the fundraiser data
    return purpose;
  };

  const calculateProgress = (collected, goal) => {
    const collectedNum = parseFloat(collected) || 0;
    const goalNum = parseFloat(goal) || 0;
    if (goalNum === 0) return 0;
    return Math.min(100, (collectedNum / goalNum) * 100);
  };

  const handleCreateNewFundraiser = () => {
    if (user) {
      navigate("/detailsScreen", { 
        state: { 
          userId: user.id || user.userId 
        } 
      });
    } else {
      navigate("/start-fundraiser");
    }
  };

  const handleEditFundraiser = (fundraiserId) => {
    navigate(`/edit-fundraiser/${fundraiserId}`);
  };

  const handleViewFundraiser = (fundraiserId) => {
    navigate(`/campaign/${fundraiserId}`);
  };

  const refreshFundraisers = () => {
    if (userId) {
      fetchDashboardData(userId);
    } else if (user) {
      fetchUserIdAndDashboard(user.email);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">My Fundraisers</h1>
                <p className="text-gray-500 text-sm">
                  Manage and track all your fundraising campaigns
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={refreshFundraisers}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition flex items-center"
                disabled={loading}
              >
                <i className={`fas fa-redo mr-2 ${loading ? "animate-spin" : ""}`}></i>
                Refresh
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition"
              >
                <i className="fas fa-home mr-2"></i>
                Home
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-100 text-sm">Total Fundraisers</p>
                <h3 className="text-3xl font-bold mt-2">{summary.totalFundraisers}</h3>
              </div>
              <i className="fas fa-hands-helping text-3xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Goal</p>
                <h3 className="text-3xl font-bold mt-2">
                  {formatCurrency(fundraisers.reduce((total, f) => total + parseFloat(f.goal_amount || 0), 0))}
                </h3>
              </div>
              <i className="fas fa-bullseye text-3xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Amount Raised</p>
                <h3 className="text-3xl font-bold mt-2">
                  {formatCurrency(summary.totalRaised)}
                </h3>
              </div>
              <i className="fas fa-chart-line text-3xl opacity-80"></i>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">My Donations</p>
                <h3 className="text-3xl font-bold mt-2">
                  {formatCurrency(summary.totalDonated)}
                </h3>
              </div>
              <i className="fas fa-heart text-3xl opacity-80"></i>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-800">
                My Fundraising Campaigns
              </h2>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mb-4"></div>
              <p className="text-gray-500">Loading your fundraisers...</p>
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="fas fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
              <p className="text-red-600 font-medium mb-2">{error}</p>
              <button
                onClick={refreshFundraisers}
                className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition"
              >
                <i className="fas fa-redo mr-2"></i>
                Try Again
              </button>
            </div>
          ) : fundraisers.length === 0 ? (
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-50 mb-4">
                <i className="fas fa-hands-helping text-3xl text-teal-500"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No Fundraisers Yet
              </h3>
              <p className="text-gray-500 mb-6">
                You haven't created any fundraisers yet. Start your first campaign and make a difference!
              </p>
              <button
                onClick={handleCreateNewFundraiser}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition shadow-lg"
              >
                <i className="fas fa-rocket mr-2"></i>
                Start Your First Fundraiser
              </button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {fundraisers.map((fundraiser) => (
                <div key={fundraiser.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left: Campaign Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {fundraiser.title || `Campaign #${fundraiser.id}`}
                            </h3>
                            {getStatusBadge(fundraiser.status)}
                          </div>
                          
                          
                          {/* Progress Bar */}
                          <div className="mb-3">
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-gray-600">
                                Raised: {formatCurrency(fundraiser.collected_amount)}
                              </span>
                              <span className="text-gray-600">
                                Goal: {formatCurrency(fundraiser.goal_amount)}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-gradient-to-r from-teal-500 to-teal-600 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${calculateProgress(fundraiser.collected_amount, fundraiser.goal_amount)}%` }}
                              ></div>
                            </div>
                            <div className="text-right text-xs text-gray-500 mt-1">
                              {calculateProgress(fundraiser.collected_amount, fundraiser.goal_amount).toFixed(1)}% Complete
                            </div>
                          </div>

                          {/* Meta Info */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center text-gray-600">
                              <i className="fas fa-calendar-alt mr-2 text-gray-400"></i>
                              Created: {formatDate(fundraiser.created_at)}
                            </div>
                            <div className="flex items-center text-gray-600">
                              <i className="fas fa-hashtag mr-2 text-gray-400"></i>
                              ID: {fundraiser.id}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex flex-col sm:flex-row md:flex-col gap-2">
                     
                      
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Summary Footer */}
          {fundraisers.length > 0 && (
            <div className="bg-gray-50 p-6 border-t">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-gray-500">Active/Approved Campaigns</p>
                  <h4 className="text-xl font-bold text-gray-800">
                    {fundraisers.filter(f => f.status === "approved" || f.status === "active").length}
                  </h4>
                </div>
                <div>
                  <p className="text-sm text-gray-500">In Draft/Submitted</p>
                  <h4 className="text-xl font-bold text-gray-800">
                    {fundraisers.filter(f => f.status === "draft" || f.status === "submitted" || f.status === "pending").length}
                  </h4>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Average Completion</p>
                  <h4 className="text-xl font-bold text-gray-800">
                    {fundraisers.length > 0 
                      ? (fundraisers.reduce((sum, f) => sum + calculateProgress(f.collected_amount, f.goal_amount), 0) / fundraisers.length).toFixed(1)
                      : 0}%
                  </h4>
                </div>
              </div>
            </div>
          )}
        </div>    
      </div>
    </div>
  );
}